// ============================================
// ALL IMPORTS AT THE TOP (ONLY ONCE!)
// ============================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  ShoppingCart, 
  Eye, 
  Star, 
  Package,        // ← Import Package here ONCE
  X, 
  ExternalLink, 
  Clock,
  PackageIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// ============================================
// 1. UTILITY FUNCTIONS FOR LOCALSTORAGE
// ============================================

export const recentlyViewedUtils = {
  get: () => {
    try {
      const items = localStorage.getItem('recentlyViewed');
      return items ? JSON.parse(items) : [];
    } catch {
      return [];
    }
  },
  
  add: (productId) => {
    try {
      let items = recentlyViewedUtils.get();
      items = items.filter(id => id !== productId);
      items.unshift(productId);
      items = items.slice(0, 8);
      localStorage.setItem('recentlyViewed', JSON.stringify(items));
    } catch (error) {
      console.error('Failed to save recently viewed:', error);
    }
  },
  
  clear: () => {
    try {
      localStorage.removeItem('recentlyViewed');
    } catch (error) {
      console.error('Failed to clear recently viewed:', error);
    }
  }
};

export const filterPreferencesUtils = {
  get: () => {
    try {
      const prefs = localStorage.getItem('filterPreferences');
      return prefs ? JSON.parse(prefs) : { sortBy: 'featured' };
    } catch {
      return { sortBy: 'featured' };
    }
  },
  
  save: (preferences) => {
    try {
      localStorage.setItem('filterPreferences', JSON.stringify(preferences));
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  }
};


// ============================================
// 2. NO IMAGE COMPONENT
// ============================================

// ❌ REMOVE THIS LINE: import { Package } from 'lucide-react';

export const NoImagePlaceholder = ({ productName }) => (
  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
    <Package size={48} className="text-gray-600 mb-2" />
    <p className="text-gray-500 text-sm text-center px-4">No Image Available</p>
  </div>
);


// ============================================
// 3. SKELETON LOADING COMPONENT
// ============================================

export const ProductCardSkeleton = () => (
  <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden animate-pulse">
    <div className="w-full h-64 md:h-72 lg:h-80 bg-gray-800"></div>
    <div className="p-4 space-y-3">
      <div className="flex justify-between">
        <div className="h-4 bg-gray-800 rounded w-20"></div>
        <div className="h-4 bg-gray-800 rounded w-16"></div>
      </div>
      <div className="h-5 bg-gray-800 rounded w-3/4"></div>
      <div className="h-6 bg-gray-800 rounded w-24"></div>
    </div>
  </div>
);

export const SkeletonGrid = ({ count = 12 }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
    {Array.from({ length: count }).map((_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </div>
);


// ============================================
// 4. FILTER CHIPS COMPONENT
// ============================================

// ❌ REMOVE THIS LINE: import { X } from 'lucide-react';

export const FilterChips = ({ 
  selectedCategory, 
  selectedType, 
  priceRange, 
  sortBy,
  onClearCategory,
  onClearType,
  onClearPrice,
  onClearSort,
  onClearAll
}) => {
  // ... rest of FilterChips code
};


// ============================================
// 5. QUICK VIEW MODAL
// ============================================

// ❌ REMOVE THESE LINES:
// import { ShoppingCart, ExternalLink, Star, Package as PackageIcon } from 'lucide-react';
// import { useState, useEffect } from 'react';

export const QuickViewModal = ({ productId, onClose, onAddToCart }) => {
  const [product, setProduct] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (productId) {
      fetchProductDetails();
    }
  }, [productId]);

  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      const [productRes, mediaRes] = await Promise.all([
        axios.get(`${API}/products/${productId}`),
        axios.get(`${API}/products/${productId}/media`)
      ]);
      
      setProduct(productRes.data);
      if (mediaRes.data.success) {
        const images = mediaRes.data.media.filter(m => m.file_type === 'image');
        setMedia(images);
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  if (!productId) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-red-600"></div>
          </div>
        ) : product ? (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-gray-900 border-b border-white/10 p-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold">Quick View</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="grid md:grid-cols-2 gap-6 p-6">
              {/* Left - Images */}
              <div>
                <div className="relative aspect-square bg-gray-800 rounded-lg overflow-hidden mb-4">
                  {media.length > 0 ? (
                    <img 
                      src={media[currentImageIndex]?.file_url} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <NoImagePlaceholder productName={product.name} />
                  )}
                  
                  {product.stock === 0 && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <span className="text-white font-bold text-xl">OUT OF STOCK</span>
                    </div>
                  )}
                </div>

                {/* Thumbnail strip */}
                {media.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {media.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition ${
                          currentImageIndex === index 
                            ? 'border-red-600' 
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <img 
                          src={item.file_url} 
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right - Details */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-red-500 font-semibold">{product.category}</span>
                    <span className="text-gray-500">•</span>
                    <span className="text-sm text-gray-400">{product.type}</span>
                  </div>
                  
                  {product.brand && (
                    <p className="text-sm text-gray-400 mb-2">by {product.brand}</p>
                  )}
                  
                  <h3 className="text-2xl font-bold mb-2">{product.name}</h3>
                  
                  {/* Rating */}
                  {product.rating > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star 
                            key={i} 
                            size={16} 
                            className={i < Math.floor(product.rating) ? 'fill-yellow-500 text-yellow-500' : 'text-gray-600'}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-400">
                        {product.rating.toFixed(1)} ({product.review_count} reviews)
                      </span>
                    </div>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-center gap-3">
                  {product.discount_price ? (
                    <>
                      <span className="text-3xl font-bold text-red-500">
                        ₹{product.discount_price.toLocaleString()}
                      </span>
                      <span className="text-xl text-gray-500 line-through">
                        ₹{product.price.toLocaleString()}
                      </span>
                      <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-semibold">
                        {Math.round((1 - product.discount_price / product.price) * 100)}% OFF
                      </span>
                    </>
                  ) : (
                    <span className="text-3xl font-bold accent-red">
                      ₹{product.price.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Stock Status */}
                <div className="flex items-center gap-2">
                  <PackageIcon size={18} className={product.stock > 0 ? 'text-green-500' : 'text-red-500'} />
                  <span className={`text-sm font-semibold ${product.stock > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {product.stock === 0 ? 'Out of Stock' : 
                     product.stock < 5 ? `Only ${product.stock} left in stock` : 
                     'In Stock'}
                  </span>
                </div>

                {/* Description */}
                {/* <div>
                  <h4 className="font-semibold mb-2">Description</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {product.description}
                  </p>
                </div> */}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => onAddToCart(product)}
                    disabled={product.stock === 0}
                    className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ShoppingCart size={20} />
                    <span>Add to Cart</span>
                  </button>
                  
                  <a
                    href={`/product/${product.id}`}
                    className="btn-secondary flex items-center justify-center gap-2 px-6"
                  >
                    <ExternalLink size={20} />
                    <span>View Details</span>
                  </a>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-400">Product not found</p>
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================
// 6. ENHANCED PRODUCT CARD
// ============================================

// ❌ REMOVE THIS LINE: import { Heart, Eye } from 'lucide-react';

// Replace your EnhancedProductCard in ShopComponents.jsx with this:

// Replace your EnhancedProductCard in ShopComponents.jsx with this:

export const EnhancedProductCard = ({ 
  product, 
  isInWishlist, 
  onWishlistToggle,
  onQuickView,
  onAddToCart 
}) => {
  const [productMedia, setProductMedia] = useState([]);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);

  useEffect(() => {
    fetchProductMedia();
  }, [product.id]);

  const fetchProductMedia = async () => {
    if (mediaLoaded || mediaLoading) return;
    
    setMediaLoading(true);
    try {
      const response = await axios.get(`${API}/products/${product.id}/media`);
      if (response.data.success && response.data.media.length > 0) {
        const mediaList = response.data.media;
        const primary = mediaList.find(media => media.is_primary && media.file_type === 'image');
        const images = mediaList.filter(media => media.file_type === 'image');
        
        setPrimaryImage(primary || images[0]);
        setProductMedia(images);
        setMediaLoaded(true);
      } else {
        setMediaLoaded(true);
      }
    } catch (error) {
      console.error('Failed to fetch product media:', error);
      setMediaLoaded(true);
    } finally {
      setMediaLoading(false);
    }
  };

  useEffect(() => {
    if (!isHovering || productMedia.length <= 1) {
      setCurrentImageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % productMedia.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [isHovering, productMedia.length]);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleDotClick = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  const getCurrentImage = () => {
    if (productMedia.length === 0) return null;
    if (isHovering && productMedia.length > 1) {
      return productMedia[currentImageIndex]?.file_url;
    }
    return primaryImage?.file_url || productMedia[0]?.file_url;
  };

  const currentImage = getCurrentImage();
  const discountPercent = product.discount_price 
    ? Math.round((1 - product.discount_price / product.price) * 100)
    : 0;

  const handleCardClick = (e) => {
    recentlyViewedUtils.add(product.id);
  };

  const handleQuickViewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onQuickView(product.id);
  };

  const handleAddToCartClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onAddToCart(product);
  };

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onWishlistToggle(product.id, e);
  };

  return (
    <div
      className="group relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`product-card-${product.id}`}
    >
      <Link
        to={`/product/${product.id}`}
        className="block bg-white/5 rounded-lg overflow-hidden border border-white/10 transition-all duration-300 hover:border-red-500/30 hover:shadow-[0_8px_30px_rgba(220,38,38,0.2)] hover:-translate-y-1"
        onClick={handleCardClick}
      >
        {/* Image Container */}
        <div className="relative aspect-square bg-gray-900 overflow-hidden">
          {/* Main Image */}
          {currentImage ? (
            <img 
              src={currentImage} 
              alt={product.name} 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <NoImagePlaceholder productName={product.name} />
          )}
          
          {/* Loading Spinner */}
          {mediaLoading && !mediaLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-red-600"></div>
            </div>
          )}

          {/* Top Left - Discount Badge (Minimal) */}
          {discountPercent > 0 && (
            <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-0.5 rounded text-xs font-semibold">
              -{discountPercent}%
            </div>
          )}

          {/* Top Right - Wishlist (Minimal) */}
          <button
            onClick={handleWishlistClick}
            className={`absolute top-2 right-2 p-1.5 rounded-full backdrop-blur-sm transition-all ${
              isInWishlist 
                ? 'bg-red-600 text-white' 
                : 'bg-black/40 text-white hover:bg-black/60'
            }`}
            title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            type="button"
          >
            <Heart size={16} className={isInWishlist ? 'fill-white' : ''} />
          </button>

          {/* Bottom Left - Stock Badge (Minimal) */}
          {product.stock === 0 ? (
            <div className="absolute bottom-2 left-2 bg-red-600/90 text-white px-2 py-0.5 rounded text-xs font-semibold backdrop-blur-sm">
              Out of Stock
            </div>
          ) : product.stock < 5 ? (
            <div className="absolute bottom-2 left-2 bg-yellow-600/90 text-white px-2 py-0.5 rounded text-xs font-semibold backdrop-blur-sm">
              {product.stock} left
            </div>
          ) : (
            <div className="absolute bottom-2 left-2 bg-green-600/90 text-white px-2 py-0.5 rounded text-xs font-semibold backdrop-blur-sm">
              In Stock
            </div>
          )}

          {/* Image Carousel Dots (Only if multiple images) */}
          {productMedia.length > 1 && (
            <div className="absolute bottom-2 right-2 flex gap-1 z-10">
              {productMedia.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => handleDotClick(index, e)}
                  className={`transition-all ${
                    currentImageIndex === index 
                      ? 'w-4 h-1.5 bg-red-500 rounded-full' 
                      : 'w-1.5 h-1.5 bg-white/50 rounded-full hover:bg-white/80'
                  }`}
                  aria-label={`View image ${index + 1}`}
                  type="button"
                />
              ))}
            </div>
          )}

          {/* Center Action Buttons - Transparent, No Blur */}
          <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <button
              onClick={handleQuickViewClick}
              className="p-3 bg-white/20 backdrop-blur-md border border-white/30 rounded-full hover:bg-white/30 hover:scale-110 transition-all shadow-lg pointer-events-auto"
              type="button"
              title="Quick View"
            >
              <Eye size={20} className="text-white drop-shadow-lg" />
            </button>
            
            {product.stock > 0 && (
              <button
                onClick={handleAddToCartClick}
                className="p-3 bg-red-600/80 backdrop-blur-md border border-red-500/50 rounded-full hover:bg-red-600/90 hover:scale-110 transition-all shadow-lg pointer-events-auto"
                type="button"
                title="Add to Cart"
              >
                <ShoppingCart size={20} className="text-white drop-shadow-lg" />
              </button>
            )}
          </div>

          {/* Out of Stock Overlay */}
          {product.stock === 0 && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-white font-semibold text-sm">OUT OF STOCK</span>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-red-500 font-semibold uppercase tracking-wide">
              {product.category}
            </span>
            <span className="text-xs text-gray-500">{product.type}</span>
          </div>
          
          {product.brand && (
            <p className="text-xs text-gray-500 mb-1">by {product.brand}</p>
          )}
          
          <h3 className="text-sm font-semibold mb-1.5 line-clamp-2 leading-tight">
            {product.name}
          </h3>
          
          {/* Rating */}
          {product.rating > 0 && (
            <div className="flex items-center gap-1 mb-2">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star 
                    key={i} 
                    size={12} 
                    className={i < Math.floor(product.rating) ? 'fill-yellow-500 text-yellow-500' : 'text-gray-600'}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500">
                ({product.review_count})
              </span>
            </div>
          )}
          
          {/* Price */}
          <div className="flex items-baseline gap-2">
            {product.discount_price ? (
              <>
                <span className="text-lg font-bold text-red-500">
                  ₹{product.discount_price.toLocaleString()}
                </span>
                <span className="text-xs text-gray-500 line-through">
                  ₹{product.price.toLocaleString()}
                </span>
              </>
            ) : (
              <span className="text-lg font-bold text-white">
                ₹{product.price.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </Link>

    </div>
  );
};


// ============================================
// 7. RECENTLY VIEWED SECTION
// ============================================

// ❌ REMOVE THIS LINE: import { Clock } from 'lucide-react';

// Add RecentlyViewedCard component
const RecentlyViewedCard = ({ product }) => {
  const [primaryImage, setPrimaryImage] = useState(null);
  
  useEffect(() => {
    const fetchImage = async () => {
      try {
        const res = await axios.get(`${API}/products/${product.id}/media`);
        if (res.data.success && res.data.media.length > 0) {
          const primary = res.data.media.find(m => m.is_primary && m.file_type === 'image');
          setPrimaryImage(primary?.file_url || res.data.media[0]?.file_url);
        }
      } catch (error) {
        console.error('Failed to fetch image:', error);
      }
    };
    fetchImage();
  }, [product.id]);

  return (
    <Link
      to={`/product/${product.id}`}
      className="group bg-white/5 rounded-lg overflow-hidden border border-white/10 hover:border-red-500/50 transition"
    >
      <div className="aspect-square bg-gray-900 relative overflow-hidden">
        {primaryImage ? (
          <img 
            src={primaryImage} 
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
        ) : (
          <NoImagePlaceholder productName={product.name} />
        )}
      </div>
      <div className="p-3">
        <p className="text-xs text-red-500 font-semibold mb-1">{product.category}</p>
        <h4 className="text-sm font-semibold line-clamp-2 mb-2">{product.name}</h4>
        <p className="text-lg font-bold accent-red">₹{product.price.toLocaleString()}</p>
      </div>
    </Link>
  );
};

export const RecentlyViewedSection = ({ onProductClick }) => {
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentlyViewed();
  }, []);

  const fetchRecentlyViewed = async () => {
    const recentIds = recentlyViewedUtils.get();
    if (recentIds.length === 0) {
      setLoading(false);
      return;
    }

    try {
      const productPromises = recentIds.map(id => 
        axios.get(`${API}/products/${id}`).catch(() => null)
      );
      const results = await Promise.all(productPromises);
      const products = results
        .filter(res => res !== null)
        .map(res => res.data);
      setRecentProducts(products);
    } catch (error) {
      console.error('Failed to fetch recently viewed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || recentProducts.length === 0) return null;

  return (
    <div className="mt-16 pt-8 border-t border-white/10">
      <div className="flex items-center gap-2 mb-6">
        <Clock size={24} className="text-red-500" />
        <h2 className="text-2xl font-bold">Recently Viewed</h2>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {recentProducts.map(product => (
          <RecentlyViewedCard key={product.id} product={product} />
        ))}
      </div>
    </div>
  );
};